/**
 * REGISTRO DE IDIOMAS — a única fonte de verdade sobre quais idiomas existem.
 *
 * Por que este módulo existe: até 2026-08-21 a lista de idiomas estava
 * duplicada em cinco lugares (`SUPPORTED_LANGUAGES`, `URL_PREFIX_LANGUAGES`,
 * o mapa `flags`, `INTL_LOCALES`/`DATE_FNS_LOCALES` e o array literal do
 * `LanguageSelector`). O japonês foi adicionado em quatro deles e esquecido no
 * quinto — resultado: `/ja` existia, mas NENHUM usuário conseguia chegar lá
 * pela interface. Duplicação de lista é a classe do bug; o conserto é ter uma
 * lista só e derivar todo o resto dela.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * PARA ADICIONAR UM IDIOMA (critério I1 da RUBRICA-QA):
 *   1. acrescente a entrada em `LANGUAGES` abaixo;
 *   2. crie `src/locales/{code}.json` com TODAS as chaves de `pt.json`;
 *   3. se for escrita não-latina, registre a fonte (ver `src/lib/cjk-font.ts`)
 *      e o `Record<Language, Locale>` do date-fns em `src/lib/format-locale.ts`
 *      — o TypeScript passa a exigir a nova chave nesses dois Records;
 *   4. libere as rotas no `vercel.json` (a matriz de publicação é por rota).
 * Nada mais precisa ser tocado: seletor, bandeiras, `hreflang`, `Intl` e
 * paridade de dicionário saem daqui automaticamente, e os testes em
 * `src/lib/__tests__/languages.test.ts` + `src/components/__tests__/
 * LanguageSelector.test.tsx` falham se algum consumidor ficar para trás.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Módulo sem dependências (nem i18next, nem React, nem date-fns) de propósito:
 * qualquer camada pode importá-lo sem arrastar o runtime de i18n junto.
 */

/**
 * `label` é o endônimo — o nome do idioma NA GRAFIA DELE (critério C3 da
 * rubrica): quem só lê japonês precisa reconhecer 「日本語」 na lista, não
 * "Japonês". `urlPrefix: false` marca o idioma que vive na raiz do site.
 */
export const LANGUAGES = [
  { code: 'pt', label: 'Português', flag: '🇧🇷', urlPrefix: false, intl: 'pt-BR', html: 'pt-BR', hreflang: 'pt-BR', ogLocale: 'pt_BR' },
  { code: 'en', label: 'English', flag: '🇺🇸', urlPrefix: true, intl: 'en-US', html: 'en', hreflang: 'en', ogLocale: 'en_US' },
  { code: 'es', label: 'Español', flag: '🇪🇸', urlPrefix: true, intl: 'es-ES', html: 'es', hreflang: 'es', ogLocale: 'es_ES' },
  { code: 'ja', label: '日本語', flag: '🇯🇵', urlPrefix: true, intl: 'ja-JP', html: 'ja', hreflang: 'ja', ogLocale: 'ja_JP' },
] as const

export type LanguageDefinition = (typeof LANGUAGES)[number]

/** Todo idioma que o site suporta. */
export type Language = LanguageDefinition['code']

/** Idiomas que vivem sob prefixo de URL (`/ja`, `/en`, `/es`) — PT é a raiz. */
export type UrlPrefixLanguage = Extract<LanguageDefinition, { urlPrefix: true }>['code']

/** Idiomas suportados, na ordem de exibição do seletor. */
export const SUPPORTED_LANGUAGES: readonly Language[] = LANGUAGES.map((l) => l.code)

/** Idiomas com prefixo de URL. */
export const URL_PREFIX_LANGUAGES: readonly UrlPrefixLanguage[] = LANGUAGES.filter(
  (l): l is Extract<LanguageDefinition, { urlPrefix: true }> => l.urlPrefix,
).map((l) => l.code)

/** O idioma da raiz do site — o único sem prefixo. */
export const DEFAULT_LANGUAGE: Language = 'pt'

function byCode<K extends keyof LanguageDefinition>(key: K): Record<Language, LanguageDefinition[K]> {
  return Object.fromEntries(LANGUAGES.map((l) => [l.code, l[key]])) as Record<
    Language,
    LanguageDefinition[K]
  >
}

/** Endônimos para o seletor (`日本語`, não "Japonês"). */
export const LANGUAGE_LABELS = byCode('label')

/** Bandeira exibida ao lado do endônimo. */
export const LANGUAGE_FLAGS = byCode('flag')

/** Tag BCP-47 usada pelo `Intl` — `pt` é pt-BR, não pt-PT. */
export const INTL_LOCALES = byCode('intl')

/** Valor de `<html lang>`. */
export const HTML_LANGS = byCode('html')

/** Valor do atributo `hreflang` de cada alternativa. */
export const HREFLANGS = byCode('hreflang')

/** `og:locale` usa underscore e território (padrão do Open Graph). */
export const OG_LOCALES = byCode('ogLocale')

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

export function isUrlPrefixLanguage(value: unknown): value is UrlPrefixLanguage {
  return typeof value === 'string' && (URL_PREFIX_LANGUAGES as readonly string[]).includes(value)
}
